/**
 * The projection anchor date — where "Year 1" starts, and how it is resolved from the two places a
 * caller can specify it.
 *
 * Anchors are DAY-PRECISE. They were rounded back to the first of the month until 2026-08-06, on the
 * grounds that strategy periods are month-based to match the fact-find's `YYYY-MM` fields — a capital
 * expense recorded as `2027-04` has no day, so the bands it is tested against were given none either.
 * The cost was that a projection run on the 6th opened on the 1st and told the adviser so, charging the
 * client for five days before they had walked in the door.
 *
 * Only the ANCHOR is day-precise; the fact-find's `YYYY-MM` fields still are not, and
 * `sumCapitalExpensesForProjectionPeriod` reconciles the two by comparing a `YYYY-MM` expense against
 * the MONTH the period opens in. So an expense pencilled in for the opening month still counts in full,
 * which is the lenient reading and the one that keeps a mid-month anchor from silently dropping it.
 *
 * What day precision cost elsewhere: the twelve monthly growth instalments assume a period opens on an
 * instalment boundary, and a period opening on the 6th skips its month's instalment entirely. See
 * {@link openingMonthGrowthFraction}, the stub that makes up the difference.
 *
 * Extracted from `financialCalculations.ts` when the expense assessment moved to `expenses.ts`, which
 * needs these too — keeping them there would have made the two modules import each other's values at
 * runtime. They are re-exported from `financialCalculations.ts`, where existing callers import
 * {@link resolveProjectionAnchorDate} from.
 */

import type { StrategyOptions } from '../../../../../shared/src/calculation/strategyOptionsTypes.ts';

/** Default anchor for "strategy Year 1": **today** (local time), stripped of its clock. */
export function getDefaultProjectionAnchorDate(): Date {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

/**
 * Strip an anchor's time of day, keeping its date.
 *
 * A projected period is a whole number of days, and the engine measures it with `daysInclusive`, which
 * reads only the date parts. An anchor carrying a clock would still compare correctly there but would
 * misbehave against the `Date` comparisons the care-start and capital-expense rules use, so it is
 * normalised once, here, rather than defended against at each of them.
 */
export function normalizeProjectionAnchorToDayStart(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Resolve the projection anchor `Date` (day-precise) from optional `strategyOptions.projectionAnchorDate`.
 */
export function resolveProjectionAnchorDate(strategyOptions: StrategyOptions = {}): Date {
    const raw = strategyOptions?.projectionAnchorDate;
    if (raw != null && raw !== '') {
        const d = raw instanceof Date ? new Date(raw.getTime()) : new Date(raw);
        if (!Number.isNaN(d.getTime())) return normalizeProjectionAnchorToDayStart(d);
    }
    return getDefaultProjectionAnchorDate();
}

/**
 * The same resolution from a `projectionContext` rather than `strategyOptions` — the projection engine
 * carries the anchor on that instead. Unlike {@link resolveProjectionAnchorDate} it does not clone a
 * `Date` it is given, which is safe because every caller only reads it.
 */
export function resolveAnchorFromProjectionContext(projectionContext: any = {}): Date {
    const raw = projectionContext.projectionAnchorDate;
    if (raw != null && raw !== '') {
        const d = raw instanceof Date ? raw : new Date(raw);
        if (!Number.isNaN(d.getTime())) return normalizeProjectionAnchorToDayStart(d);
    }
    return getDefaultProjectionAnchorDate();
}
